import React, { useState, useEffect } from "react";
import Select from "react-select";
import AddItemButton from "../../common/components/base/AddItemButton";
import ItemsGrid from "../../common/components/widgets/ItemsGrid";
import LoadingSpinner from "../../common/components/LoadingSpinner/LoadingSpinner";
import Swal from "sweetalert2";
import { useRegions } from "../../hooks/useRegions";
import { useJobProfilesByRegion } from "../../hooks/useJobProfilesByRegion";
import { useSkillDimensionsByJobProfile } from "../../hooks/useSkillDimensionsByJobProfile";
import { useKnowledgeItemsByDimension } from "../../hooks/useKnowledgeItemsByDimension";
import { useDeleteKnowledgeItem } from "../../hooks/useDeleteKnowledgeItem";
import AbilityKnowledgeModal from "./AbilityKnowledgeModal";

const columns = ["name", "type", "unitOfCompetence", "professionalQualification", "region", "action"];
const columnsName = ["Name", "Type", "Unit of Competence", "Professional Qualification", "Region", "Actions"];

const AbilitiesKnowledgesTab = () => {
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [selectedJobProfile, setSelectedJobProfile] = useState(null);
  const [selectedDimension, setSelectedDimension] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [isUpdate, setIsUpdate] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const { data: regionsData } = useRegions();
  const { data: jobProfilesData } = useJobProfilesByRegion(selectedRegion?.value);
  const { data: dimensionsData } = useSkillDimensionsByJobProfile(selectedJobProfile?.value);
  const { data: knowledgeItemsData, isFetching: loading } = useKnowledgeItemsByDimension(
    selectedDimension?.value
  );
  const deleteMutation = useDeleteKnowledgeItem();

  const regions = regionsData?.regions || [];
  const jobProfiles = jobProfilesData?.jobProfiles || [];
  const dimensions = dimensionsData?.skillDimensions || [];
  const knowledgeItems = knowledgeItemsData?.knowledgeItems || [];

  const regionOptions = regions.map((r) => ({
    value: r.key || r._id,
    label: r.name,
  }));

  const jobProfileOptions = jobProfiles.map((jp) => ({
    value: jp.key || jp._id,
    label: jp.name,
  }));

  const dimensionOptions = dimensions.map((d) => ({
    value: d.key || d._id,
    label: d.name,
  }));

  useEffect(() => {
    setSelectedJobProfile(null);
  }, [selectedRegion]);

  useEffect(() => {
    setSelectedDimension(null);
  }, [selectedJobProfile]);

  const items = knowledgeItems.map((ki) => ({
    ...ki,
    unitOfCompetence: selectedDimension?.label || "",
    professionalQualification: selectedJobProfile?.label || "",
    region: selectedRegion?.label || "",
  }));

  const handleCreate = () => {
    setSelectedItem(null);
    setIsUpdate(false);
    setOpenModal(true);
  };

  const handleEdit = (item) => {
    setSelectedItem(item);
    setIsUpdate(true);
    setOpenModal(true);
  };

  const handleDelete = (item) => {
    Swal.fire({
      title: "Are you sure you want to delete this Ability / Knowledge?",
      showDenyButton: true,
      showCancelButton: true,
      confirmButtonText: "Yes",
      denyButtonText: "No",
    }).then((result) => {
      if (result.value === true) {
        deleteMutation.mutate(
          { id: item.key || item._id },
          {
            onSuccess: () => {
              Swal.fire({
                icon: "success",
                title: "Ability / Knowledge deleted!",
                showConfirmButton: false,
                timer: 1500,
              });
            },
            onError: (error) => {
              Swal.fire({
                icon: "error",
                title: "Error",
                text: error?.response?.data?.message || error.message,
                showConfirmButton: true,
              });
            },
          }
        );
      }
    });
  };

  return (
    <div>
      <AddItemButton OnClick={handleCreate} disabled={openModal} />

      <div className="row" style={{ marginBottom: "16px", marginTop: "16px" }}>
        <div className="col-sm-12 col-md-4">
          <Select
            value={selectedRegion}
            onChange={(value) => setSelectedRegion(value)}
            options={regionOptions}
            isClearable
            placeholder="Select Region"
          />
        </div>
        <div className="col-sm-12 col-md-4">
          <Select
            value={selectedJobProfile}
            onChange={(value) => setSelectedJobProfile(value)}
            options={jobProfileOptions}
            isClearable
            isDisabled={!selectedRegion}
            placeholder="Select Professional Qualification"
          />
        </div>
        <div className="col-sm-12 col-md-4">
          <Select
            value={selectedDimension}
            onChange={(value) => setSelectedDimension(value)}
            options={dimensionOptions}
            isClearable
            isDisabled={!selectedJobProfile}
            placeholder="Select Unit of Competence"
          />
        </div>
      </div>

      {loading && <LoadingSpinner />}

      {selectedRegion && selectedJobProfile && selectedDimension && !loading && (
        <ItemsGrid
          columns={columns}
          columnsName={columnsName}
          items={items}
          edit={(index, value) => handleEdit(value)}
          remove={(index, value) => handleDelete(value)}
          title="Abilities & Knowledges"
          rowsPerPage={10}
          pagination={true}
        />
      )}

      <AbilityKnowledgeModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        isUpdate={isUpdate}
        item={selectedItem}
        defaultRegion={selectedRegion}
        defaultJobProfile={selectedJobProfile}
        defaultDimension={selectedDimension}
      />
    </div>
  );
};

export default AbilitiesKnowledgesTab;
